import { MapUtils } from '../../../utility/map-utils.js';
import { DataModelBuilder, SerializationContext, SerializedData } from '../../dto/dto.js';
import { fromEnumAsString, toEnumFromString } from '../../../serialization/enum-utils.js';
import { InvoiceProvider } from './invoice-provider.js';
import { LegacyInvoiceDetails } from './legacy-invoice-details.js';
import { InvoiceBase } from '../../../contracts/invoice.js';
import { Dayjs } from 'dayjs';
import { TimeZone } from '../../../time/timezone.js';
import { VersionRange } from '../../core/version-range.js';
import { SerializationException } from '../../../exceptions.js';

export class Invoice extends InvoiceBase {
  static readonly TYPE_ID: string = '17968d635bcf40b3a70ef63e1ff7dd1c';
  static readonly SCHEMA_VERSION: number = 2;

  private constructor(
    public readonly provider: InvoiceProvider,
    public readonly uniqueId: string,
    public readonly name: string,
    public readonly downloadUrl: string | null,
    public readonly creationDate: Dayjs,
  ) {
    super();
  }

  get $typeId() {
    return Invoice.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, Invoice.SCHEMA_VERSION);
  }

  get sanitizedName(): string | undefined {
    let name = this.name;

    if(!name) {
      return name;
    }

    name = name.normalize('NFD').replace(/\p{Diacritic}/gu, '');
    return name.replace(/[^a-zA-Z0-9]/g, '_');
  }

  static create(options: {
    provider: InvoiceProvider,
    uniqueId: string,
    name: string,
    downloadUrl: string | null,
    creationDate: Dayjs,
  }): Invoice {
    return new Invoice(
        options.provider,
        options.uniqueId,
        options.name,
        options.downloadUrl,
        options.creationDate,
    );
  }

  static fromLegacy(legacyInvoice: LegacyInvoiceDetails, creationDate: Dayjs): Invoice {
    return new Invoice(
        legacyInvoice.provider,
        legacyInvoice.uniqueId,
        legacyInvoice.name,
        legacyInvoice.downloadUrl,
        TimeZone.cloneDate(creationDate),
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): Invoice {
    return new Invoice(
        toEnumFromString(MapUtils.get<string>(map, 'provider'), InvoiceProvider),
        MapUtils.getString(map, 'uniqueId'),
        MapUtils.getString(map, 'name'),
        MapUtils.getStringOrNull(map, 'downloadUrl'),
        context.decodeDate(MapUtils.getInteger(map, 'creationDate')),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'provider': fromEnumAsString(this.provider),
      'uniqueId': this.uniqueId,
      'name': this.name,
      'downloadUrl': this.downloadUrl,
      'creationDate': context.encodeDate(this.creationDate),
    };
  }

  clone(): this {
    return new Invoice(
        this.provider,
        this.uniqueId,
        this.name,
        this.downloadUrl,
        TimeZone.cloneDate(this.creationDate),
    ) as this;
  }

  isSame(other: Invoice): boolean {
    if(this === other) {
      return true;
    }

    return this.provider === other.provider &&
      this.uniqueId === other.uniqueId &&
      this.name === other.name &&
      TimeZone.isSameInstant(this.creationDate, other.creationDate) &&
      this.downloadUrl === other.downloadUrl;
  }
}

export class InvoiceBuilder extends DataModelBuilder<Invoice> {
  build(context: SerializationContext, map: SerializedData): Invoice {
    return Invoice.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);
    if(versionRange.contains(Invoice.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(Invoice.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In this version we added field [creationDate] of type [Dayjs]
    if(version < 2) {
      // This value should be passed down by [OnlinePaymentDetailsBuilder.migrateToCurrentSchemaVersion].
      // If shit is broken, check there 👆
      const invoiceCreationDate = context.storage.get<Dayjs>('invoiceCreationDate');
      if(invoiceCreationDate == null) {
        throw new SerializationException('Failed to migrate invoice. Creation date not available.');
      }

      map['creationDate'] = context.encodeDate(invoiceCreationDate);
      version = 2;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
